import React, { useCallback } from 'react';
import type { MemoizedFn } from 'memoize-one';
import memoizeOne from 'memoize-one';

import { valueSeparator } from '@atlassian/search-common/transformAnalyticsQueryParamToObject';
import { useBackendExperimentContext } from '@atlassian/search-experiment/backend-experiment-context';

import { addQueryParams } from '@confluence/route-manager-utils/entry-points/url.ts';
import { hashData } from '@confluence/hash';

import { useSearchInputContext } from '../../SearchInputProvider.tsx';
import { SearchResultType } from '../../gql/__types__/AdvancedSearchAGGTypes.ts';
import type {
	AdvancedSearchResultAncestor,
	AdvancedSearchResultContent,
	AdvancedSearchResultSpace,
	AdvancedSearchResultType,
} from '../../AdvancedSearch-types.ts';
import { useAnalytics } from '../../AnalyticsProvider.tsx';

import { ContentResult } from './ContentResult.tsx';
import { SpaceResult } from './SpaceResult.tsx';
import { SearchResultItem } from './styled.tsx';

interface SearchResultProps {
	searchResult: AdvancedSearchResultType;
	searchResultIdx: number;
}

export const processSearchResult: MemoizedFn<
	(
		result: AdvancedSearchResultType,
		url: string,
	) => {
		url: string;
		entityType: SearchResultType | string;
		subtype?: string;
		title: string;
		excerpt: string;
		lastModified: string;
		content: AdvancedSearchResultContent;
		space: AdvancedSearchResultSpace;
		ancestors?: AdvancedSearchResultAncestor[];
		emoji?: string;
		isVerified?: boolean;
	}
> = memoizeOne(
	(
		result: AdvancedSearchResultType,
		url: string,
	): {
		url: string;
		entityType: SearchResultType | string;
		subtype?: string;
		title: string;
		excerpt: string;
		lastModified: string;
		content: AdvancedSearchResultContent;
		space: AdvancedSearchResultSpace;
		ancestors?: AdvancedSearchResultAncestor[];
		emoji?: string;
		isVerified?: boolean;
	} => ({
		...result,
		url,
	}),
);

export const getUrlWithQueryParams = ({
	searchResult,
	searchSessionId,
	searchQuery,
}: {
	searchResult: AdvancedSearchResultType;
	searchSessionId: string;
	searchQuery?: string;
}): string => {
	let url = addQueryParams(`${searchResult.url}`, {
		search_id: searchSessionId,
	});

	if (searchQuery) {
		const analyticsFormattedForUrlParsing = `queryHash${valueSeparator}${hashData(searchQuery)}`;
		// manually doing the query params instead of using the helper function from search common due to lack of
		// addqueryparams support/overlap
		url = addQueryParams(`${searchResult.url}`, {
			search_id: searchSessionId,
			additional_analytics: analyticsFormattedForUrlParsing,
		});
	}
	return url;
};
export const SearchResult = ({
	searchResult,
	searchResultIdx,
}: SearchResultProps): React.JSX.Element => {
	const { fireAnalytics, searchSessionId } = useAnalytics();
	const { entityType, content, space } = searchResult;
	const searchInputs = useSearchInputContext();
	const { searchQuery } = searchInputs;
	const backendExperiment = useBackendExperimentContext();

	const fireClickEvent = useCallback(() => {
		fireAnalytics({
			type: 'sendUIEvent',
			actionSubject: 'advancedSearchResultLink',
			action: 'clicked',
			additionalAttributes: {
				abTest: backendExperiment,
				position: searchResultIdx,
				metadata: {
					entityType: entityType !== 'space' ? 'content' : 'space',
					contentType: entityType !== 'space' && content.type,
					contentId: entityType !== 'space' && content.id,
					spaceId: entityType !== 'space' && content.space.id,
					containerId: entityType !== 'space' ? content.space?.id : space?.id,
				},
			},
		});
	}, [fireAnalytics, entityType, content, space, searchResultIdx, backendExperiment]);

	const fireContextClickEvent = useCallback(() => {
		fireAnalytics({
			type: 'sendUIEvent',
			actionSubject: 'advancedSearchResultLink',
			action: 'contextMenuClicked',
			additionalAttributes: {
				abTest: backendExperiment,
				position: searchResultIdx,
				metadata: {
					entityType: entityType !== 'space' ? 'content' : 'space',
					contentType: entityType !== 'space' && content.type,
					contentId: entityType !== 'space' && content.id,
					spaceId: entityType !== 'space' && content.space.id,
					containerId: entityType !== 'space' ? content.space?.id : space?.id,
				},
			},
		});
	}, [fireAnalytics, entityType, content, space, searchResultIdx, backendExperiment]);

	const url = getUrlWithQueryParams({
		searchQuery,
		searchSessionId,
		searchResult,
	});
	const processedSearchResult = processSearchResult(searchResult, url);

	const searchResultTypeComponent =
		entityType === SearchResultType.space ? (
			<SpaceResult
				searchResult={processedSearchResult}
				onClickResult={fireClickEvent}
				onContextClickResult={fireContextClickEvent}
			/>
		) : (
			<ContentResult
				searchResult={processedSearchResult}
				searchResultIdx={searchResultIdx}
				onClickResult={fireClickEvent}
				onContextClickResult={fireContextClickEvent}
			/>
		);

	return <SearchResultItem>{searchResultTypeComponent}</SearchResultItem>;
};
